'use client';

import { Text, useTranslation } from '@weareconceptstudio/core';
import { useRouter } from 'next/navigation';
import { useEffect, useCallback, useState, useRef } from 'react';
import PopupPromotionRegisterStyle from './style';
import Button from '@/components/common/Button';
import Icon from '../Icon';

const SESSION_STORAGE_KEY = 'popup-earn-promotion-register';

const CopyIcon = () => (
	<svg
		className='icon-copy'
		xmlns='http://www.w3.org/2000/svg'
		viewBox='0 0 24 24'
		fill='none'>
		<path
			d='M9.00021 18.0002C8.737 18.004 8.47583 17.9537 8.2328 17.8526C7.98977 17.7514 7.77005 17.6016 7.58721 17.4122C7.39815 17.2294 7.24853 17.0098 7.14757 16.7669C7.04662 16.5241 6.99647 16.2632 7.00021 16.0002V4.00021C6.99633 3.73708 7.04641 3.47596 7.14737 3.23294C7.24833 2.98991 7.39802 2.77011 7.58721 2.58718C7.77013 2.398 7.98989 2.24832 8.23291 2.14737C8.47594 2.04641 8.73708 1.99633 9.00021 2.00021H18.0002C18.2634 1.99646 18.5246 2.04672 18.7676 2.14785C19.0106 2.24899 19.2304 2.39886 19.4132 2.58822C19.6023 2.77103 19.7519 2.99064 19.8528 3.23349C19.9538 3.47633 20.004 3.73724 20.0002 4.00021V16.0002C20.004 16.2632 19.9538 16.5241 19.8528 16.7669C19.7519 17.0098 19.6023 17.2294 19.4132 17.4122C19.2304 17.6016 19.0106 17.7514 18.7676 17.8526C18.5246 17.9537 18.2634 18.004 18.0002 18.0002H9.00021ZM5.00021 22.0002C4.73708 22.0041 4.47594 21.954 4.23291 21.8531C3.98989 21.7521 3.77013 21.6024 3.58721 21.4132C3.39802 21.2303 3.24833 21.0105 3.14737 20.7675C3.04641 20.5245 2.99633 20.2633 3.00021 20.0002V7.00021C3.00021 6.44792 3.44792 6.00021 4.00021 6.00021H5.00021V19.0002C5.00021 19.5525 5.44792 20.0002 6.00021 20.0002H16.0002V21.0002C16.0002 21.5525 15.5525 22.0002 15.0002 22.0002H5.00021Z'
			fill='#26B4AE'
		/>
	</svg>
);

const PopupPromotionRegister = ({ promotion }) => {
	const copyTimeoutRef = useRef(null);
	const [isOpen, setIsOpen] = useState(false);
	const [isCopied, setIsCopied] = useState(false);
	const { translate } = useTranslation();
	const router = useRouter();

	const promoCode = promotion?.code?.trim();
	const promoName = promotion?.name?.trim();
	const promoSubtitle = translate('promotionRegisterPopupSubtitle');
	const promoDescription =
		promotion?.message?.trim() ||
		promotion?.banner_title?.trim() ||
		translate('promotionRegisterPopupTitle');

	const handleClose = useCallback(() => {
		setIsOpen(false);
		sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
	}, []);

	const handleCopyPromoCode = useCallback(async () => {
		if (!promoCode) {
			return;
		}

		try {
			await navigator.clipboard.writeText(promoCode);
		} catch {
			const el = document.createElement('textarea');
			el.value = promoCode;
			document.body.appendChild(el);
			el.select();
			document.execCommand('copy');
			document.body.removeChild(el);
		}

		setIsCopied(true);

		if (copyTimeoutRef.current) {
			clearTimeout(copyTimeoutRef.current);
		}

		copyTimeoutRef.current = setTimeout(() => {
			setIsCopied(false);
		}, 2000);
	}, [promoCode]);

	useEffect(() => {
		if (!promoCode || sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true') {
			return;
		}

		// Delay so the popup does not block the first product taps on page load
		const openTimer = setTimeout(() => {
			setIsOpen(true);
		}, 1200);

		return () => {
			clearTimeout(openTimer);

			if (copyTimeoutRef.current) {
				clearTimeout(copyTimeoutRef.current);
			}
		};
	}, [promoCode]);

	if (!promoCode || !promoName) {
		return null;
	}

	if (!isOpen) {
		return null;
	}

	return (
		<PopupPromotionRegisterStyle>
			<div
				className='layer-background open'
				onClick={handleClose}
				aria-hidden='true'
			/>
			<div
				className='inner-wrapper open'
				role='dialog'
				aria-modal='true'
				aria-label={translate('promotionRegisterPopupTitle')}>
				<div className='inner-content'>
					<div className='popup-header'>
						<div className='wrap-icon-close'>
							<Icon
								name='close'
								className='icon-close-popup'
								onClick={handleClose}
							/>
						</div>

						{promoSubtitle ? (
							<Text
								className='popup-subtitle white h5 font-futura-medium'
								text={promoSubtitle}
							/>
						) : null}
						<Text
							className='popup-title white h2-L font-futura-medium'
							text={promoDescription}
						/>
					</div>

					<div className='popup-body'>
						<Text
							className='black h3 font-futura-regular'
							text={'promotionRegisterPopupDescription'}
						/>

						<div className='promo-code-wrapper'>
							<span className='promo-code-text'>{promoName}</span>
							<button
								type='button'
								className={`promo-code-copy ${isCopied ? 'is-copied' : ''}`}
								onClick={handleCopyPromoCode}
								aria-label='Copy promo code'>
								{isCopied ? (
									<Text
										className='promo-code-copied-text green p font-futura-medium'
										text={'promotionRegisterPopupCopied'}
									/>
								) : (
									<CopyIcon />
								)}
							</button>
						</div>

						<div className='wrapper-button'>
							<Button
								btnType='default'
								className='width-100 font-futura-medium'
								text={translate('promotionRegisterPopupButton')}
								onClick={() => {
									handleClose();
									router.push('/register');
								}}
							/>
						</div>
					</div>
				</div>
			</div>
		</PopupPromotionRegisterStyle>
	);
};

export default PopupPromotionRegister;
